import { Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FaTimes, FaUndo, FaWindowClose } from "react-icons/fa";
import ReactModal from "react-modal";
import {
  View,
  Text,
  Pressable,
  StyleSheet,
  useWindowDimensions,
} from "react-native";

const BallTracking = ({
  openZModal,
  setOpenZModal,
  setSelectedCells,
  onClearZAxis,
  action,
  selectedCells,
  onConfirm,
}) => {
  const { width } = useWindowDimensions();
  const config = {
    rows: 10,
    cols: 32,
    fieldRows: 6,
    fieldCols: 8,
    fieldStartRow: 4,
    fieldStartCol: 12,
  };

  const bottomFieldConfig = {
    rows: 3,
    cols: 8,
    startValue: 1,
  };

  const getViewportSize = () => {
    if (window.visualViewport) {
      return {
        width: window.visualViewport.width,
        height: window.visualViewport.height,
      };
    }
    return {
      width: window.innerWidth,
      height: window.innerHeight,
    };
  };

  const [viewport, setViewport] = useState(getViewportSize());

  useEffect(() => {
    const update = () => setViewport(getViewportSize());

    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    window.visualViewport.addEventListener("resize", update);

    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
      window.visualViewport.removeEventListener("resize", update);
    };
  }, []);

  const getBottomFieldValue = (row, col) => {
    // Column groups: 01–71
    const columnBase = col * 10 + 1;
    return columnBase + row;
  };

  // const cellWidth = Math.min(MAX_CELL, Math.floor(gridWidth / config.cols));
  // const cellHeight = Math.floor(cellWidth * 0.8);
  // final cell size

  const GRID_PADDING = 48;
  const HEADER_HEIGHT = 36;
  const MAX_CELL = 64;

  const availableWidth = viewport.width - GRID_PADDING;
  const availableHeight =
    viewport.height - HEADER_HEIGHT - bottomFieldConfig.rows * 32;

  const cellWidthByCols = Math.floor(availableWidth / config.cols);
  const cellHeightByRows = Math.floor(availableHeight / config.rows);

  const cellWidth = Math.min(MAX_CELL, cellWidthByCols);
  const cellHeight = Math.min(Math.floor(cellWidth * 0.8), cellHeightByRows);

  const isFieldCell = (row, col) =>
    row >= config.fieldStartRow &&
    row < config.fieldStartRow + config.fieldRows &&
    col >= config.fieldStartCol &&
    col < config.fieldStartCol + config.fieldCols;

  const isBoundary = (row, col) =>
    isFieldCell(row, col) &&
    (row === config.fieldStartRow ||
      col === config.fieldStartCol ||
      col === config.fieldStartCol + config.fieldCols - 1);

  const getZone = (row, col) => {
    if (!isFieldCell(row, col)) return "Outside Field";
    if (isBoundary(row, col)) return "Boundary";

    const relRow = row - config.fieldStartRow;
    const relCol = col - config.fieldStartCol;

    const vert = relRow < config.fieldRows / 2 ? "Top" : "Bottom";
    const horiz =
      relCol < config.fieldCols / 3
        ? "Left"
        : relCol < (config.fieldCols * 2) / 3
        ? "Center"
        : "Right";

    return `${vert} ${horiz}`;
  };

  const gridToPx = (row, col) => ({
    x: col * cellWidth,
    y: row * cellHeight,
  });

  const buildQuarterArc = ({ corner, row, col, radiusCols, radiusRows }) => {
    const center = gridToPx(row, col);

    let startX, startY, endX, endY, sweep;

    if (corner === "top-left") {
      startX = center.x;
      startY = center.y + radiusRows * cellHeight;
      endX = center.x + radiusCols * cellWidth;
      endY = center.y;
      sweep = 0;
    }

    if (corner === "top-right") {
      startX = center.x;
      startY = center.y + radiusRows * cellHeight;
      endX = center.x - radiusCols * cellWidth;
      endY = center.y;
      sweep = 1;
    }

    return (
      <path
        d={`
        M ${startX} ${startY}
        A ${radiusCols * cellWidth} ${radiusRows * cellHeight}
          0 0 ${sweep}
          ${endX} ${endY}
      `}
        stroke="#000"
        strokeWidth={2}
        fill="none"
      />
    );
  };

  const buildLine = (fromRow, fromCol, toRow, toCol) => {
    const from = gridToPx(fromRow, fromCol);
    const to = gridToPx(toRow, toCol);

    return (
      <line
        x1={from.x}
        y1={from.y}
        x2={to.x}
        y2={to.y}
        stroke="#000"
        strokeWidth={1.5}
      />
    );
  };

  const buildArc = ({ centerRow, centerCol, rRow, rCol }) => {
    const center = gridToPx(centerRow, centerCol);

    return (
      <path
        d={`
        M ${center.x - rCol * cellWidth} ${center.y}
        A ${rCol * cellWidth} ${rRow * cellHeight}
          0 0 0
          ${center.x + rCol * cellWidth} ${center.y}
      `}
        stroke="#000"
        strokeWidth={1.5}
        fill="none"
      />
    );
  };

  const handleCellClick = (id, row, col, value) => {
    setSelectedCells((prev) => {
      // if (prev.ball?.id === id) {
      //   return { ...prev, ball: null };
      // }

      // if (prev.keeper?.id === id) {
      //   return { ...prev, keeper: null };
      // }

      const cellData = {
        id,
        row,
        col,
        value,
        zone: getZone(row, col),
      };

      let newState = prev;

      if (!prev.keeper) {
        newState = { ...prev, keeper: cellData };
      } else if (!prev.ball) {
        newState = { ...prev, ball: cellData };
      } else {
        return prev;
      }

      if (newState.ball && newState.keeper) {
        setTimeout(() => {
          onConfirm(newState);
          setOpenZModal(false);
        }, 100);
      }

      return newState;
    });
  };

  const getCellBg = (isField, isBound) => {
    if (isField) return isBound ? "#374151" : "#E1EFFE";
    return "#8DA2FB";
  };

  const actionParts = action?.split("-");
  const actionType = `${actionParts?.[2]}-${actionParts?.[3]}`;

  // Categories
  const ONLY_GOAL_POST = ["CS-2", "LS-2", "H-2"].includes(actionType);
  const ONLY_INSIDE_GOAL = [
    "CS-1",
    "CS-3",
    "CS-4",
    "LS-1",
    "LS-3",
    "LS-4",
    "H-1",
    "H-3",
    "H-4",
  ].includes(actionType);
  const ONLY_OUTSIDE_GOAL = ["CS-0", "LS-0", "H-0"].includes(actionType);

  const isCellClickable = (row, col) => {
    const field = isFieldCell(row, col);
    const boundary = isBoundary(row, col);

    // Goal post only
    if (ONLY_GOAL_POST) {
      return boundary;
    }

    // Inside goal only
    if (ONLY_INSIDE_GOAL) {
      return field && !boundary;
    }

    // Outside goal only
    if (ONLY_OUTSIDE_GOAL) {
      return !field;
    }

    // allow all
    return true;
  };

  const handleModalClose = () => {
    setOpenZModal(false);
  };
  return (
    <ReactModal
      isOpen={openZModal}
      shouldCloseOnEsc
      shouldCloseOnOverlayClick
      onRequestClose={handleModalClose}
      style={{
        overlay: {
          backgroundColor: "rgba(0, 0, 0, 0.3)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backdropFilter: "blur(4px)",
        },
        content: {
          backgroundColor: "#fff",
          border: "none",
          inset: "auto",
          padding: 0,
          // borderRadius: "0.75rem",
          width: "auto",
          height: "auto",
          maxWidth: "100%",
          maxHeight: "100%",
          overflow: "auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
      }}
    >
      <View style={styles.card}>
        <View
          style={{
            display: "flex",
            flexDirection: "row",
            width: "100%",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 3,
          }}
        >
          <View
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "end",
              gap: 10,
            }}
          >
            <Typography
              style={{
                fontSize: "8px",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 2,
              }}
            >
              <div
                style={{
                  background:
                    selectedCells?.keeper && selectedCells?.ball
                      ? "#22c55e"
                      : selectedCells?.keeper
                      ? "#22c55e"
                      : "#2563eb",
                  width: "0.5rem",
                  height: "0.5rem",
                  borderRadius: "100%",
                }}
              ></div>
              {!selectedCells?.keeper && !selectedCells?.ball
                ? "Select the Keeper position."
                : selectedCells?.keeper && !selectedCells?.ball
                ? "Select the Ball position."
                : `Selected - Keeper: ${selectedCells?.keeper?.value}, Ball: ${selectedCells?.ball?.value}`}
            </Typography>
          </View>
          <View
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 16,
            }}
          >
            {(selectedCells?.ball || selectedCells?.keeper) && (
              <Pressable
                onPress={() => {
                  setSelectedCells({
                    ball: null,
                    keeper: null,
                  });
                  onClearZAxis();
                }}
                style={({ pressed }) => ({
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 4,
                  paddingVertical: 2,
                  paddingHorizontal: 6,
                  borderRadius: 4,
                  borderWidth: 1,
                  borderColor: "#fecaca",
                  backgroundColor: pressed ? "#fee2e2" : "#fff",
                  cursor: "pointer",
                })}
              >
                <Typography
                  style={{
                    fontSize: "8px",
                    color: "#ef4444",
                    fontWeight: 600,
                  }}
                >
                  Clear Selection
                </Typography>

                <FaUndo
                  style={{
                    fontSize: "8px",
                    color: "#ef4444",
                  }}
                />
              </Pressable>
            )}
            <FaTimes onClick={handleModalClose} />
          </View>
        </View>
        <View
          style={{
            position: "relative",
          }}
        >
          <svg
            width="100%"
            height="100%"
            style={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              zIndex: 100000,
            }}
          >
            {/* Outer box */}
            {buildLine(
              0,
              config.fieldStartCol - 4,
              config.rows + 2.5,
              config.fieldStartCol - 4
            )}
            {buildLine(
              0,
              config.fieldStartCol + config.fieldCols + 4,
              config.rows + 2.5,
              config.fieldStartCol + config.fieldCols + 4
            )}
            {buildLine(
              0,
              config.fieldStartCol - 4,
              0,
              config.fieldStartCol + config.fieldCols + 4
            )}
            {buildLine(
              config.rows + 2.5,
              config.fieldStartCol - 4,
              config.rows + 2.5,
              config.fieldStartCol + config.fieldCols + 4
            )}

            {/* Inner box */}
            {buildLine(
              config.fieldStartRow + config.fieldCols - 0.65,
              config.fieldStartCol,
              config.fieldStartRow + config.fieldCols - 0.65,
              config.fieldStartCol + config.fieldCols
            )}
            {buildLine(
              config.fieldStartRow + config.fieldRows + 1.4,
              config.fieldStartCol,
              config.fieldStartRow + config.fieldRows,
              config.fieldStartCol
            )}
            {buildLine(
              config.fieldStartRow + config.fieldRows + 1.4,
              config.fieldStartCol + config.fieldCols,
              config.fieldStartRow + config.fieldRows,
              config.fieldStartCol + config.fieldCols
            )}

            {/* Penalty arc */}
            {buildArc({
              centerRow: config.rows + bottomFieldConfig.rows - 0.5,
              centerCol: config.fieldStartCol + config.fieldCols / 2,
              rRow: 1,
              rCol: 1,
            })}
            {/* Top-left corner arc */}
            {buildQuarterArc({
              corner: "top-left",
              row: 10.1,
              col: 0,
              radiusCols: 0.7,
              radiusRows: 0.9,
            })}

            {/* Top-right corner arc */}
            {buildQuarterArc({
              corner: "top-right",
              row: 10.1,
              col: config.cols,
              radiusCols: 0.7,
              radiusRows: 0.9,
            })}
          </svg>

          <View
            style={[
              styles.gridWrapper,
              {
                width: cellWidth * config.cols,
                height: cellHeight * config.rows,
              },
            ]}
          >
            {Array.from({ length: config.rows }, (_, row) => (
              <View key={row} style={styles.gridRow}>
                {Array.from({ length: config.cols }, (_, col) => {
                  const id = `${row}-${col}`;
                  const value = (config.rows - 1 - row) * config.cols + col + 1;

                  const isField = isFieldCell(row, col);
                  const isBound = isBoundary(row, col);

                  const isBall = selectedCells.ball?.id === id;
                  const isKeeper = selectedCells.keeper?.id === id;

                  const clickable = isCellClickable(row, col);
                  return (
                    <Pressable
                      key={id}
                      onPress={() => handleCellClick(id, row, col, value)}
                      disabled={
                        !clickable &&
                        !selectedCells?.ball &&
                        selectedCells?.keeper
                      } // only disable for ball selection should be enable for keeper posiiton
                      style={[
                        styles.gridCell,
                        {
                          minHeight: "0.938rem",
                          minWidth: "1.189rem",
                          width: cellWidth,
                          opacity:
                            !clickable &&
                            !selectedCells?.ball &&
                            selectedCells?.keeper
                              ? 0.5
                              : 1,
                          height: cellHeight,
                          backgroundColor: isBall
                            ? "#22c55e" // green = ball
                            : isKeeper
                            ? "#2563eb" // blue = keeper
                            : getCellBg(isField, isBound),
                          // opacity: (!isBall || isKeeper) && selectedCells ? 0.9 : 1,
                        },
                      ]}
                    >
                      <Text
                        style={[
                          styles.cellText,
                          {
                            fontSize: Math.max(5, cellWidth * 0.1),
                            color: isBound || isKeeper ? "#fff" : "#1f2937",
                          },
                          (!isBall || isKeeper) && styles.cellTextBold,
                        ]}
                      >
                        {value}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>
            ))}
          </View>
          <View
            style={{
              border: "1px solid black",
              background:
                "linear-gradient(180deg, rgba(49, 196, 141, 1) 0%, rgba(222, 247, 236, 1) 70%, rgba(255, 255, 255, 1) 100%)",
            }}
          >
            {Array.from({ length: bottomFieldConfig.rows }, (_, row) => (
              <View key={row} style={{ flexDirection: "row" }}>
                {Array.from({ length: bottomFieldConfig.cols }, (_, col) => {
                  const value = getBottomFieldValue(row, col);
                  const id = `bottom-${row}-${col}`;

                  const isBall = selectedCells.ball?.id === id;
                  const isKeeper = selectedCells.keeper?.id === id;

                  return (
                    <Pressable
                      key={id}
                      // onPress={() => handleCellClick(id, row, col, value)}
                      style={{
                        // backgroundColor: isBall
                        //   ? "#22c55e"
                        //   : isKeeper
                        //   ? "#2563eb"
                        //   : "none",
                        minWidth: "4.7rem",
                        minHeight: "1.125rem",
                        width: cellWidth * 4,
                        height: cellHeight * 1.2,
                        borderWidth: 1,
                        borderColor: "#00000033",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Text style={styles.bottomCellText}>
                        {String(value).padStart(2, "0")}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>
            ))}
          </View>
        </View>

        {/* <View
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "4px",
          }}
        >
          <View
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "end",
              gap: 10,
            }}
          >
            {[
              { color: "#22c55e", text: "Ball Position" },
              { color: "#2563eb", text: "Keeper Position" },
            ].map((item, index) => (
              <View
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <div
                  style={{
                    background: item.color,
                    width: "0.5rem",
                    height: "0.5rem",
                    borderRadius: "100%",
                  }}
                ></div>
                <Text
                  style={{
                    fontSize: "10px",
                  }}
                >
                  {item.text}
                </Text>
              </View>
            ))}
          </View>
          <View
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-end",
              gap: 12,
            }}
          >
            <Pressable
              onPress={handleCancel}
              style={({ pressed }) => ({
                paddingVertical: 4,
                paddingHorizontal: 6,
                borderRadius: 6,
                borderWidth: 1,
                borderColor: "#d1d5db",
                backgroundColor: "#fff",
                opacity: pressed ? 0.8 : 1,
                cursor: "pointer",
              })}
            >
              <Text
                style={{
                  color: "#374151",
                  fontWeight: "600",
                  fontSize: "10px",
                }}
              >
                Cancel
              </Text>
            </Pressable>

            <Pressable
              disabled={!selectedCells.ball || !selectedCells.keeper}
              onPress={handleConfirm}
              style={({ pressed }) => ({
                paddingVertical: 4,
                paddingHorizontal: 6,
                borderRadius: 6,
                backgroundColor: "#2563eb",
                opacity: !selectedCells || pressed ? 0.6 : 1,
                cursor: !selectedCells ? "not-allowed" : "pointer",
              })}
            >
              <Text
                style={{
                  color: "#fff",
                  fontSize: "10px",
                  fontWeight: "600",
                }}
              >
                Confirm
              </Text>
            </Pressable>
          </View>
        </View> */}
      </View>
    </ReactModal>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: "#f9fafb",
  },
  card: {
    backgroundColor: "#fff",
    borderRadius: 12,
    // padding: 24,
    margin: 10,
    elevation: 3,
    maxHeight: "90vh",
  },
  gridWrapper: {
    // borderWidth: 2,
    border: "1px solid black",
    // borderColor: "#d1d5db",
    // borderRadius: 8,
    overflow: "hidden",
    alignSelf: "flex-start",
  },
  gridRow: {
    flexDirection: "row",
  },

  bottomCellText: {
    fontSize: 10,
    fontWeight: "700",
    color: "#0f766e",
  },

  gridCell: {
    borderWidth: "0.4px",
    cursor: "pointer",
    borderColor: "#0000002e",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
  },
  cellText: {
    fontWeight: "500",
  },
  cellTextBold: {
    fontWeight: "700",
  },
  badge: {
    position: "absolute",
    top: 2,
    right: 2,
    backgroundColor: "#ef4444",
    justifyContent: "center",
    alignItems: "center",
  },
  badgeText: {
    color: "#fff",
    fontSize: 10,
    fontWeight: "700",
  },
});

export default BallTracking;
